<script setup lang="ts">
/**
 * ChainWowCard — chain-level matched-day week comparison (home).
 *
 * Modeled on the /branch/:id compare strip (BranchSalesHistory): both week
 * absolutes with actual date windows + signed Δ฿/Δ%, matched-day semantics
 * labeled, breadth, and the branch coverage stamp. Never compares a partial
 * current week against a full prior week — only D vs D-7 pairs contribute,
 * and both columns show MATCHED totals (apples-to-apples).
 *
 * bead lucky-war-tl5y: header shortened to ยอดขายเทียบสัปดาห์ก่อน + uPlot
 * matched-day overlay (this week vs last week, per weekday) — shows WHICH
 * days drove the delta, not just the aggregate.
 *
 * Gated (no prior same-weekday): current-week absolute + ข้อมูลสัปดาห์แรก note.
 * Empty: renders nothing (HomeView already hides the sales block).
 *
 * bead: lucky-war-etcn.1 — chain WoW compare card + coverage stamps
 */
import { computed, ref, watch, nextTick, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import uPlot from 'uplot';
import 'uplot/dist/uPlot.min.css';
import type { SalesAnchorResponse, SalesTrendResponse } from '@/api';
import { fetchSalesTrend } from '@/api';
import { apiBrand, lensFromQuery, scopeLabel } from '@/lib/brand';
import { chainWowPlotSignature } from '@/lib/chainWowPlot';
import { cssVar } from '@/lib/display';
import { tooltipPlugin, tooltipBaht } from '@/lib/uplotTooltip';
import ShareButton from '@/components/ShareButton.vue';
import type { KpiCardShareInput, KpiRow } from '@/lib/shareFlex';

const props = defineProps<{ anchor: SalesAnchorResponse | null }>();

const route = useRoute();
const lens = computed(() => lensFromQuery(route.query.brand));
const trend = ref<SalesTrendResponse | null>(null);

async function loadTrend() {
  try {
    // 28d so the Monday-anchored week + its D-7 mirror always fit inside
    // the window even when the latest data lags today (POS upload delay).
    trend.value = await fetchSalesTrend(28, undefined, apiBrand(lens.value));
  } catch {
    trend.value = null; // chart is best-effort — the strip stays authoritative
  }
}
onMounted(loadTrend);
watch(() => route.query.brand, loadTrend);

// ── Date formatting (UTC-stable — avoid ICT off-by-one) ────────────────────
const THAI_SHORT_MONTHS = [
  'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
  'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.',
];

function isoToDate(iso: string): Date {
  const p = iso.split('-').map(Number);
  return new Date(Date.UTC(p[0], p[1] - 1, p[2]));
}

function fmtDayMonth(d: Date): string {
  return `${d.getUTCDate()} ${THAI_SHORT_MONTHS[d.getUTCMonth()]}`;
}

/** "11–16 ส.ค." or "31 ก.ค.–2 ส.ค." — each endpoint carries its own month. */
function windowLabel(dates: string[]): string {
  if (dates.length === 0) return '';
  const sorted = [...dates].sort();
  const first = isoToDate(sorted[0]);
  const last = isoToDate(sorted[sorted.length - 1]);
  if (sorted.length === 1) return fmtDayMonth(first);
  return `${fmtDayMonth(first)}–${fmtDayMonth(last)}`;
}

// Prior window = matched dates shifted back 7 days.
function shiftIso(iso: string, days: number): string {
  const d = isoToDate(iso);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

const curWindow = computed(() => windowLabel(props.anchor?.matched_dates ?? []));
const prevWindow = computed(() => {
  const dates = props.anchor?.matched_dates ?? [];
  if (dates.length === 0) return '';
  return windowLabel(dates.map((d) => shiftIso(d, -7)));
});

// ── Numbers ────────────────────────────────────────────────────────────────
const available = computed(() => props.anchor?.delta_available === true);

const curBaht = computed(() =>
  available.value ? props.anchor?.current_matched_net_total ?? null : props.anchor?.net_total ?? null,
);
const prevBaht = computed(() => (available.value ? props.anchor?.prior_net_total ?? null : null));
const deltaPct = computed(() => (available.value ? props.anchor?.delta_pct ?? null : null));
const deltaBaht = computed(() => (available.value ? props.anchor?.delta_baht ?? null : null));

function fmtBaht(n: number | null): string {
  return n == null ? '—' : `฿${Math.round(n).toLocaleString('th-TH')}`;
}

function fmtDeltaBaht(n: number | null): string {
  if (n == null) return '—';
  const sign = n > 0 ? '+' : n < 0 ? '−' : '';
  return `${sign}฿${Math.abs(Math.round(n)).toLocaleString('th-TH')}`;
}

function pctClass(v: number | null): string {
  if (v == null || Math.abs(v) < 0.05) return 'is-flat';
  return v > 0 ? 'is-up' : 'is-down';
}

const matchedDays = computed(() => props.anchor?.matched_days ?? 0);
// Show the partial-window note only when the comparison covers fewer than 7 days.
const partialNote = computed(() =>
  available.value && matchedDays.value > 0 && matchedDays.value < 7
    ? `เทียบจาก ${matchedDays.value} วันที่มีข้อมูลครบทั้งสองสัปดาห์`
    : '',
);

const breadth = computed(() => {
  const a = props.anchor;
  if (!available.value || !a) return '';
  return `ขึ้น ${a.branches_up} สาขา · ลง ${a.branches_down} สาขา`;
});

// ── Traffic chips (Part A, bead lucky-war-5348) ─────────────────────────────
// Bill/cust absolutes already arrive on the anchor (FULL current ISO week —
// not the matched window). Prev-week counts do NOT exist in the payload, so
// deltas are intentionally omitted: absolute-only until the backend cust_split
// bead (lucky-war-5jxz) ships prev values. Zero/missing counts hide the row.
const custCount = computed(() => props.anchor?.cust_count ?? null);
const billCount = computed(() => props.anchor?.bill_count ?? null);
const hasTraffic = computed(
  () => (billCount.value ?? 0) > 0 || (custCount.value ?? 0) > 0,
);

function fmtCount(n: number | null): string {
  return n == null ? '—' : n.toLocaleString('th-TH');
}

// Coverage stamp — numerator = branches with ≥1 sales row this week.
const coverage = computed(() => {
  const a = props.anchor;
  if (!a || a.branches_active_total <= 0) return '';
  return `มีข้อมูล ${a.branches_total}/${a.branches_active_total} สาขา`;
});

// ── LIFF share (bead lucky-war-trko) ─────────────────────────────────────────
// Matched-day WoW pair exactly as the compare strip shows it.
function shareInput(): KpiCardShareInput {
  const rows: KpiRow[] = [{ label: 'สัปดาห์นี้', value: fmtBaht(curBaht.value) }];
  if (available.value) {
    rows.push({ label: 'สัปดาห์ก่อน', value: fmtBaht(prevBaht.value) });
    if (deltaPct.value != null) {
      const sign = deltaPct.value > 0 ? '+' : deltaPct.value < 0 ? '−' : '';
      rows[rows.length - 1] = {
        label: 'สัปดาห์ก่อน',
        value: fmtBaht(prevBaht.value),
        delta: {
          text: `${sign}${Math.abs(deltaPct.value).toFixed(1)}% WoW · ${fmtDeltaBaht(deltaBaht.value)}`,
          good: (deltaPct.value ?? 0) >= 0,
        },
      };
    }
  }
  if (hasTraffic.value) {
    rows.push({ label: 'ลูกค้า / บิล (สัปดาห์นี้)', value: `${fmtCount(custCount.value)} / ${fmtCount(billCount.value)}` });
  }
  const win = available.value && curWindow.value
    ? `คู่วันจับคู่ ${curWindow.value} กับ ${prevWindow.value}`
    : coverage.value;
  return {
    title: 'ยอดขายเทียบสัปดาห์ก่อน',
    scope: scopeLabel(lens.value),
    windowLabel: win,
    note: 'ไม่รวม VAT · เทียบเฉพาะวันที่มีข้อมูลครบทั้งสองสัปดาห์',
    rows,
    linkPath: route.fullPath,
  };
}

// ── uPlot: matched-day WoW overlay (tl5y pick #2) ───────────────────────────
// Monday-anchored calendar week (จ.–อา.) fixed at the latest date with data:
// all 7 slots always render, days without data show as empty (null bars).
// Gate: comparison available + ≥2 D/D-7 pairs inside the week — fewer reads
// as noise.
const THAI_DOW = ['อา.', 'จ.', 'อ.', 'พ.', 'พฤ.', 'ศ.', 'ส.'];

const PLOT_HEIGHT = 130;
const plotHost = ref<HTMLElement | null>(null);
let plot: uPlot | null = null;
let resizeObserver: ResizeObserver | null = null;
let plotSig = '';

const fmtBahtAxis = (v: number) => {
  if (Math.abs(v) >= 1_000_000) return `฿${Math.round(v / 1_000_000)}M`;
  if (Math.abs(v) >= 1_000) return `฿${Math.round(v / 1000)}k`;
  return `฿${Math.round(v)}`;
};

const wowSpec = computed<{
  cur: (number | null)[];
  prev: (number | null)[];
  labels: string[];
  curDates: string[];
  prevDates: string[];
  curWin: string;
  prevWin: string;
} | null>(() => {
  if (!available.value) return null;
  const days = trend.value?.days ?? [];
  if (days.length === 0) return null;
  const map = new Map(days.map((d) => [d.date, d.net_total]));
  // Anchor at the LATEST date that has data — never "today": the trend can
  // trail the calendar when POS upload lags, and a today-anchored week then
  // shows the stale tail instead of the freshest days.
  const latest = days
    .filter((d) => d.net_total != null)
    .sort((a, b) => (a.date < b.date ? -1 : 1))
    .at(-1)?.date;
  if (!latest) return null;
  // Monday-anchored calendar week (Thai convention: จ.–อา.): always 7 slots
  // Mon..Sun, missing days render as empty slots (null bars), never dropped.
  const monday = isoToDate(latest);
  monday.setUTCDate(monday.getUTCDate() - ((monday.getUTCDay() + 6) % 7));
  const slotDates: string[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setUTCDate(d.getUTCDate() + i);
    slotDates.push(d.toISOString().slice(0, 10));
  }
  const cur: (number | null)[] = [];
  const prev: (number | null)[] = [];
  const labels: string[] = [];
  const curDates: string[] = [];
  const prevDates: string[] = [];
  let pairs = 0;
  for (const iso of slotDates) {
    const prevIso = shiftIso(iso, -7);
    const curVal = map.get(iso) ?? null;
    const prevVal = map.get(prevIso) ?? null;
    if (curVal != null && prevVal != null) pairs++;
    cur.push(curVal);
    prev.push(prevVal);
    // Axis: weekday + day-of-month (current week) — compact "อา. 17".
    labels.push(`${THAI_DOW[isoToDate(iso).getUTCDay()]} ${isoToDate(iso).getUTCDate()}`);
    // Tooltip: full day-month for EACH series (the pair spans two weeks).
    curDates.push(`${THAI_DOW[isoToDate(iso).getUTCDay()]} ${fmtDayMonth(isoToDate(iso))}`);
    prevDates.push(`${THAI_DOW[isoToDate(prevIso).getUTCDay()]} ${fmtDayMonth(isoToDate(prevIso))}`);
  }
  // Calm-state gate: an all-empty or single-pair chart reads as noise.
  if (pairs < 2) return null;
  return {
    cur,
    prev,
    labels,
    curDates,
    prevDates,
    curWin: windowLabel(slotDates),
    prevWin: windowLabel(slotDates.map((d) => shiftIso(d, -7))),
  };
});

function buildWowOptions(width: number, spec: NonNullable<typeof wowSpec.value>): uPlot.Options {
  const labels = spec.labels;
  const bars = (align: -1 | 1) =>
    uPlot.paths.bars ? uPlot.paths.bars({ size: [0.38], align }) : undefined;
  return {
    width,
    height: PLOT_HEIGHT,
    cursor: { drag: { x: false, y: false, setScale: false }, focus: { prox: 12 } },
    legend: { live: false, isolate: false },
    plugins: [
      tooltipPlugin((u, idx) => {
        const lines: string[] = [];
        const cur = u.data[1][idx] as number | null;
        const prev = u.data[2][idx] as number | null;
        if (cur != null) lines.push(`สัปดาห์นี้ · ${spec.curDates[idx] ?? ''} · ${tooltipBaht(cur)}`);
        if (prev != null) lines.push(`สัปดาห์ก่อน · ${spec.prevDates[idx] ?? ''} · ${tooltipBaht(prev)}`);
        return lines.join('\n');
      }),
    ],
    // Pad the x range one index per side: bars offset by align ±1 extend past
    // the first/last data point, and without padding the canvas clips them —
    // the edge weekdays then showed only one bar of the pair (bug 2026-08-18).
    scales: { x: { time: false, range: (_u, min, max) => [min - 1, max + 1] } },
    series: [
      { label: 'วัน' },
      {
        label: `สัปดาห์นี้${spec.curWin ? ` (${spec.curWin})` : ''}`,
        // Solid gold bars (PO 2026-08-29 graph-darkening pass): the old
        // --sales-soft fill rendered the current week washed-out against the
        // intentionally ghosted prior-week bars.
        stroke: cssVar('--sales'),
        fill: cssVar('--sales'),
        width: 0,
        spanGaps: false,
        paths: bars(-1),
      },
      {
        label: `สัปดาห์ก่อน${spec.prevWin ? ` (${spec.prevWin})` : ''}`,
        stroke: cssVar('--text-muted'),
        fill: cssVar('--surface-2'),
        width: 0,
        spanGaps: false,
        paths: bars(1),
      },
    ],
    axes: [
      {
        stroke: cssVar('--text-faint'),
        grid: { show: false },
        font: '11px Sarabun, sans-serif',
        // Padded-range guard positions (-1, n) render as blank, not '—'.
        values: (_u, vals) => vals.map((v) => (v == null || v < 0 || v >= labels.length ? '' : labels[Math.round(v)])),
      } as uPlot.Axis,
      {
        stroke: cssVar('--text-faint'),
        grid: { stroke: cssVar('--border-hair') },
        font: '11px Sarabun, sans-serif',
        values: (_u, vals) => vals.map((v) => (v == null ? '—' : fmtBahtAxis(v))),
        size: 44,
      } as uPlot.Axis,
    ],
  };
}

function destroyPlot() {
  plot?.destroy();
  plot = null;
  plotSig = '';
}

function syncPlot() {
  const spec = wowSpec.value;
  if (!spec || !plotHost.value) return;
  const arrays = [spec.cur.map((_, i) => i), spec.cur, spec.prev] as unknown as uPlot.AlignedData;
  const sig = chainWowPlotSignature(spec, apiBrand(lens.value));
  if (plot && sig === plotSig) {
    plot.setData(arrays);
    return;
  }
  destroyPlot();
  plotSig = sig;
  const width = plotHost.value.clientWidth || 600;
  plot = new uPlot(buildWowOptions(width, spec), arrays, plotHost.value);
  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver ??= new ResizeObserver((entries) => {
      const w = Math.round(entries[0]?.contentRect.width ?? 0);
      if (w > 0 && plot) plot.setSize({ width: w, height: PLOT_HEIGHT });
    });
    resizeObserver.observe(plotHost.value);
  }
}

watch([wowSpec, plotHost], async () => {
  await nextTick();
  if (!wowSpec.value || !plotHost.value) {
    destroyPlot();
    return;
  }
  syncPlot();
});

onUnmounted(() => {
  destroyPlot();
  resizeObserver?.disconnect();
  resizeObserver = null;
});
</script>

<template>
  <section
    v-if="anchor && !anchor.is_empty"
    class="cwc"
    :aria-label="`ยอดขายเทียบสัปดาห์ก่อน · ${scopeLabel(lens)}`"
  >
    <h2 class="card-head">ยอดขายเทียบสัปดาห์ก่อน · {{ scopeLabel(lens) }}</h2>
    <ShareButton :build="shareInput" />

    <!-- Compare strip (branch-style) -->
    <div v-if="available" class="cwc__compare">
      <div class="cwc__col">
        <span class="cwc__label">สัปดาห์นี้<template v-if="curWindow"> ({{ curWindow }})</template></span>
        <span class="cwc__value">{{ fmtBaht(curBaht) }}</span>
      </div>
      <div class="cwc__col">
        <span class="cwc__label">สัปดาห์ก่อน<template v-if="prevWindow"> ({{ prevWindow }})</template></span>
        <span class="cwc__value">{{ fmtBaht(prevBaht) }}</span>
      </div>
      <div class="cwc__col cwc__col--delta">
        <span class="cwc__label">เทียบสัปดาห์ก่อน</span>
        <span class="cwc__value" :class="pctClass(deltaPct)">
          <template v-if="deltaPct != null">{{ deltaPct > 0 ? '+' : deltaPct < 0 ? '−' : '' }}{{ Math.abs(deltaPct).toFixed(1) }}%</template>
          <template v-else>—</template>
          <span v-if="deltaPct != null" aria-hidden="true">{{ deltaPct > 0 ? ' ▲' : deltaPct < 0 ? ' ▼' : '' }}</span>
        </span>
        <span class="cwc__sub">{{ fmtDeltaBaht(deltaBaht) }}</span>
        <span v-if="breadth" class="cwc__sub">{{ breadth }}</span>
      </div>
    </div>

    <!-- Gated: absolute only, no fake comparison -->
    <div v-else class="cwc__gated">
      <span class="cwc__value">{{ fmtBaht(curBaht) }}</span>
      <span class="cwc__gated-note">ข้อมูลสัปดาห์แรก — ยังไม่สามารถเทียบสัปดาห์ก่อนได้</span>
    </div>

    <!-- Traffic chips (Part A, bead lucky-war-5348): full-week POS absolutes.
         Counts cover ALL ingested days of the week — deliberately NOT the
         matched window above, so they are labeled สัปดาห์นี้, never as the
         compared pair. -->
    <div v-if="hasTraffic" class="cwc__traffic" aria-label="จำนวนลูกค้าและบิลสัปดาห์นี้">
      <div class="cwc__chip">
        <span class="cwc__chip-label">ลูกค้าสัปดาห์นี้</span>
        <span class="cwc__chip-num">{{ fmtCount(custCount) }} <small>คน</small></span>
      </div>
      <div class="cwc__chip">
        <span class="cwc__chip-label">บิลสัปดาห์นี้</span>
        <span class="cwc__chip-num">{{ fmtCount(billCount) }} <small>บิล</small></span>
      </div>
    </div>

    <!-- Matched-day WoW overlay (uPlot, tl5y) -->
    <div
      v-if="wowSpec"
      class="cwc__chart"
      role="img"
      :aria-label="`กราฟเทียบยอดขายรายวัน สัปดาห์นี้กับสัปดาห์ก่อน ${deltaPct != null ? `เปลี่ยนแปลง ${deltaPct.toFixed(1)}%` : ''}`"
    >
      <div ref="plotHost" class="cwc__plot"></div>
    </div>

    <!-- Honesty footer: matched-day semantics + coverage + provenance -->
    <p class="cwc__foot">
      <template v-if="available">เทียบวันเดียวกันของสัปดาห์ก่อน<template v-if="partialNote"> · {{ partialNote }}</template></template>
      <template v-if="coverage"> · {{ coverage }}</template>
      · ข้อมูลจากระบบ POS
    </p>
  </section>
</template>

<style scoped>
.cwc {
  margin-bottom: 1rem;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius-card);
  padding: 0.85rem;
  box-shadow: none;
  border-top: 3px solid var(--sales);
}

/* Header: global .card-head (canonical card title, 2026-08-18). */

.cwc__compare {
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 10px;
  align-items: center;
  padding: 10px 12px;
  background: var(--surface-2);
  border: 1px solid var(--line);
  border-radius: var(--radius-chip);
}

.cwc__col {
  display: grid;
  gap: 2px;
  min-width: 0;
}

.cwc__label {
  color: var(--text-muted);
  font: 600 0.68rem/1.3 var(--font-display, 'Kanit', 'Sarabun', sans-serif);
  white-space: nowrap;
}

.cwc__value {
  color: var(--text);
  font: 600 1.05rem/1.2 var(--font-display, 'Kanit', 'Sarabun', sans-serif);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.cwc__value.is-up { color: var(--ok); }
.cwc__value.is-down { color: var(--color-bad); }
.cwc__value.is-flat { color: var(--text-muted); }

.cwc__sub {
  color: var(--text-muted);
  font: 500 0.68rem/1.4 var(--font-body, 'Sarabun', sans-serif);
  font-variant-numeric: tabular-nums;
}

.cwc__col--delta { text-align: right; }

.cwc__gated {
  display: flex;
  align-items: baseline;
  gap: 0.6rem;
  flex-wrap: wrap;
  padding: 10px 12px;
  background: var(--surface-2);
  border: 1px solid var(--line);
  border-radius: var(--radius-chip);
}

.cwc__gated .cwc__value { font-size: 1.3rem; }

.cwc__gated-note {
  color: var(--text-muted);
  font: 500 0.7rem/1.4 var(--font-body, 'Sarabun', sans-serif);
}

/* ── Traffic chips (Part A, bead lucky-war-5348) ─────────────────────────── */
.cwc__traffic {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 0.6rem;
}

.cwc__chip {
  display: grid;
  gap: 2px;
  min-width: 0;
  padding: 8px 10px;
  background: var(--surface-2);
  border: 1px solid var(--line);
  border-radius: var(--radius-chip);
}

.cwc__chip-label {
  color: var(--text-muted);
  font: 600 0.68rem/1.3 var(--font-display, 'Kanit', 'Sarabun', sans-serif);
  white-space: nowrap;
}

.cwc__chip-num {
  color: var(--text);
  font: 600 1rem/1.2 var(--font-display, 'Kanit', 'Sarabun', sans-serif);
  font-variant-numeric: tabular-nums;
}

.cwc__chip-num small {
  font-size: 0.72rem;
  font-weight: 500;
  color: var(--text-muted);
}

.cwc__foot {
  margin: 0.55rem 0 0;
  font: var(--text-source, 500 0.64rem/1.4 var(--font-body, 'Sarabun', sans-serif));
  color: var(--text-muted);
  line-height: 1.4;
}

/* ── WoW overlay chart (tl5y) ───────────────────────────────────────────── */
.cwc__chart {
  margin-top: 0.6rem;
}

.cwc__plot {
  width: 100%;
}

@media (max-width: 480px) {
  .cwc__compare { grid-template-columns: 1fr 1fr; }
  .cwc__col--delta { grid-column: 1 / -1; text-align: left; }
}
</style>
